import { Dispatch, MouseEvent, SetStateAction, useContext, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useTheme, Box, IconButton } from "@mui/material";
import Divider, { dividerClasses } from '@mui/material/Divider';
import { listClasses } from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { paperClasses } from '@mui/material/Paper';
import Select, { SelectChangeEvent } from "@mui/material/Select";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import AccountIcon from "@mui/icons-material/PersonOutlined";
import PharmaciesIcon from '@mui/icons-material/LocalPharmacyOutlined';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import { ColorModeContext, tokens } from "../theme";
import { api, apiAccounts, apiPharmacies, currentPharmacyKey, IAccount, IPharmacy, tokenKey } from '../types';
import { useAlert } from './MainAlert';
import { parseJwtToken, createApiHeaders } from '../utilities/JwtTokenParser';

interface MainTopbarProps {
  setCurrentPharmacy: Dispatch<SetStateAction<null | string>>;
}

const fetchPharmacyList = async (accountId: number): Promise<IPharmacy[]> => {
  const data = await fetch(`${api}${apiAccounts}${accountId}/${apiPharmacies}`, {
    method: 'GET',
    headers: createApiHeaders(true, false)
  });

  const json = await data.json();
  return json;
}

const MainTopbar = ({ setCurrentPharmacy }: MainTopbarProps) => {
  const navigate = useNavigate();
  const alert = useAlert();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);

  const account = parseJwtToken() as IAccount;
  const [pharmacies, setPharmacies] = useState<IPharmacy[]>([]);
  const [currentPharmacy, setPharmacy] = useState(pharmacies?.length > 0 ? `${pharmacies[0].id}` : '0');

  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const settingsMenuOpen = Boolean(settingsMenuAnchorEl);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(accountMenuAnchorEl);

  useEffect(() => {
    fetchPharmacyList(account.id)
      .then((response) => { 
        const typedResponse = response as IPharmacy[];
        const selectedPharmacy = sessionStorage.getItem(currentPharmacyKey);

        let defaultPharmacy: IPharmacy | undefined = undefined;
        if (selectedPharmacy) {
          defaultPharmacy = typedResponse.find(ph => ph.subscriptionCode === selectedPharmacy);  
        } else if (typedResponse?.length > 0) {
          defaultPharmacy = typedResponse.find(ph => ph.isAccountDefault) ?? typedResponse[0];
        }

        setPharmacies(typedResponse);
        setPharmacy(defaultPharmacy ? `${defaultPharmacy.id}` : '0');
        sessionStorage.setItem(currentPharmacyKey, defaultPharmacy?.subscriptionCode ?? '');
        setCurrentPharmacy(defaultPharmacy?.subscriptionCode ?? '');
      })
      .catch((error) => {
        alert.error(`Error while loading pharmacies. ${error}`);
      }); 
  }, []);

  const handlePharmacyChange = (event: SelectChangeEvent) => { 
    setPharmacy(event.target.value);

    const pharmacy = pharmacies.find(ph => ph.id === parseInt(event.target.value));
    sessionStorage.setItem(currentPharmacyKey, pharmacy?.subscriptionCode ?? "");
    setCurrentPharmacy(pharmacy?.subscriptionCode ?? "");
  };

  const getPharmacyName = (pharmacyId: number) : string => { 
    const pharmacy = pharmacies.find(ph => ph.id === pharmacyId);
    return pharmacy?.name ?? "UNKNOWN";
  };

  const openSettingsMenu  = (event: MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchorEl(event.currentTarget);
  };

  const openAccountMenu  = (event: MouseEvent<HTMLElement>) => {
    setAccountMenuAnchorEl(event.currentTarget);
  };

  const closeSettingsMenu = () => {
    setSettingsMenuAnchorEl(null);
  };

  const closeAccountMenu = () => {
    setAccountMenuAnchorEl(null);
  };

  const logout = () => {
    sessionStorage.removeItem(tokenKey);
    sessionStorage.removeItem(currentPharmacyKey);
    closeAccountMenu();
    navigate("/");

    // Force refresh.
    navigate(0); 
  };

  return (
    <Box display="flex" justifyContent="space-between" p={2}>
      <Box display="flex">
        <Select 
          sx={{
            backgroundColor: colors.blueAccent[700], 
            color: colors.grey[100], 
            width: 320, 
            fontSize: "15px",
            padding: "0px 3px",
          }} 
          value={currentPharmacy} 
          onChange={handlePharmacyChange}
          renderValue={(selected) => getPharmacyName(parseInt(selected))}
        >
          {pharmacies.map((pharmacy) => ( 
            <MenuItem
              sx={{
                height: 50
              }}
              key={pharmacy.id}
              value={pharmacy.id}
            >
              <ListItemIcon>
                <PharmaciesIcon />
              </ListItemIcon>
              <ListItemText primary={pharmacy.name} />
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box display="flex">
        <IconButton onClick={colorMode.toggleColorMode}>
          {theme.palette.mode === "dark" ? (<LightModeIcon />) : (<DarkModeIcon />)}
        </IconButton>

        <IconButton onClick={openSettingsMenu}>
          <SettingsIcon />
        </IconButton>
        <Menu 
          anchorEl={settingsMenuAnchorEl}
          id="settingsMenu"
          open={settingsMenuOpen}
          onClose={closeSettingsMenu}
          onClick={closeSettingsMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={closeSettingsMenu}>UI Settings (N/A)</MenuItem>
          <MenuItem onClick={closeSettingsMenu}>Application Settings (N/A)</MenuItem>
        </Menu>

        <IconButton onClick={openAccountMenu}>
          <AccountIcon />
        </IconButton>
        <Menu 
          anchorEl={accountMenuAnchorEl}
          id="accountMenu"
          open={accountMenuOpen}
          onClose={closeAccountMenu}
          onClick={closeAccountMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={{
            [`& .${listClasses.root}`]: { padding: '4px' },
            [`& .${paperClasses.root}`]: { padding: 0 },
            [`& .${dividerClasses.root}`]: { margin: '4px -4px' }
          }}
        >
          <MenuItem onClick={closeAccountMenu}>Account: {account.name}</MenuItem>
          <MenuItem onClick={closeAccountMenu}>Email: {account.email}</MenuItem>
          <Divider />
          <MenuItem 
            onClick={logout}
            sx={{
              [`& .${listItemIconClasses.root}`]: {
                ml: 'auto',
                minWidth: 0,
              },
            }}
          >
            <ListItemText>LOG OUT</ListItemText>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default MainTopbar;